import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../services/api";

interface Survey {
  id: string;
  title: string;
  owner_id: string;
  status: string;
  budget: string;
  target_responses: number;
  actual_responses: number;
  created_at: string;
}

interface SurveysResponse {
  surveys: Survey[];
  total: number;
  limit: number;
  offset: number;
}

export default function AdminSurveysPage() {
  const [filters, setFilters] = useState({
    status: "",
  });
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "surveys", filters, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });
      if (filters.status) params.append("status", filters.status);

      const response = await api.get<{ success: boolean; data: SurveysResponse }>(
        `/admin/surveys?${params.toString()}`
      );
      return response.data.data;
    },
  });

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: "bg-gray-500/20 text-gray-400",
      active: "bg-green-500/20 text-green-400",
      completed: "bg-blue-500/20 text-blue-400",
      cancelled: "bg-red-500/20 text-red-400",
    };
    return badges[status as keyof typeof badges] || "bg-gray-500/20 text-gray-400";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Surveys</h1>
        <p className="text-gray-400 mt-2">
          {data?.total || 0} total surveys
        </p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setPage(0);
              }}
              className="w-full input"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Surveys Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading surveys...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-border">
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Title</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Owner</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Status</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Budget</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Responses</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {data?.surveys.map((survey) => (
                  <tr key={survey.id} className="border-b border-dark-border hover:bg-dark-700">
                    <td className="py-4 px-6">
                      <div className="text-white font-medium">{survey.title}</div>
                      <div className="text-gray-500 text-sm">{survey.id.slice(0, 8)}...</div>
                    </td>
                    <td className="py-4 px-6 text-gray-400">
                      {survey.owner_id.slice(0, 8)}...
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(survey.status)}`}>
                        {survey.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-white">
                      ${parseFloat(survey.budget).toFixed(2)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-white">
                        {survey.actual_responses} / {survey.target_responses}
                      </div>
                      <div className="text-gray-500 text-sm">
                        {survey.target_responses > 0
                          ? `${((survey.actual_responses / survey.target_responses) * 100).toFixed(0)}%`
                          : "0%"}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-400">
                      {new Date(survey.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!data?.surveys.length && (
              <div className="text-center py-12 text-gray-400">No surveys found</div>
            )}
          </div>
        )}

        {/* Pagination */}
        {data && data.total > limit && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-dark-border">
            <div className="text-gray-400 text-sm">
              Showing {page * limit + 1} to {Math.min((page + 1) * limit, data.total)} of {data.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-4 py-2 bg-dark-700 text-white rounded hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * limit >= data.total}
                className="px-4 py-2 bg-dark-700 text-white rounded hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
